import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import LogPerformance from "@/pages/LogPerformance";
import Entries from "@/pages/Entries";
import EntryDetail from "@/pages/EntryDetail";
import Progress from "@/pages/Progress";
import Profile from "@/pages/Profile";
import Settings from "@/pages/Settings";
import CoachDashboard from "@/pages/CoachDashboard";
import CoachPlayer from "@/pages/CoachPlayer";
import AdminDashboard from "@/pages/AdminDashboard";
import PublicProfile from "@/pages/PublicProfile";
import { InstallPrompt } from "@/components/InstallPrompt";
import { Logo } from "@/components/Logo";

function Splash() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="animate-pulse">
        <Logo size={44} />
      </div>
    </div>
  );
}

function homeFor(role) {
  if (role === "coach") return "/coach";
  if (role === "admin") return "/admin";
  return "/app/dashboard";
}

function Protected({ role }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to={homeFor(user.role)} replace />;
  if (user.role === "player" && !user.onboarding_complete) return <Navigate to="/onboarding" replace />;
  return <Outlet />;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Splash />;
  if (user) return <Navigate to={homeFor(user.role)} replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<PublicOnly><Landing /></PublicOnly>} />
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/p/:username" element={<PublicProfile />} />

          <Route element={<Protected role="player" />}>
            <Route element={<AppShell />}>
              <Route path="/app/dashboard" element={<Dashboard />} />
              <Route path="/app/log" element={<LogPerformance />} />
              <Route path="/app/log/:id" element={<LogPerformance />} />
              <Route path="/app/entries" element={<Entries />} />
              <Route path="/app/entries/:id" element={<EntryDetail />} />
              <Route path="/app/progress" element={<Progress />} />
              <Route path="/app/profile" element={<Profile />} />
              <Route path="/app/settings" element={<Settings />} />
            </Route>
          </Route>

          <Route element={<Protected role="coach" />}>
            <Route element={<AppShell />}>
              <Route path="/coach" element={<CoachDashboard />} />
              <Route path="/coach/player/:playerId" element={<CoachPlayer />} />
              <Route path="/coach/profile" element={<Profile />} />
            </Route>
          </Route>

          <Route element={<Protected role="admin" />}>
            <Route element={<AppShell />}>
              <Route path="/admin" element={<AdminDashboard />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <InstallPrompt />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
